import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Button,
    Table, TableBody, TableCell, TableContainer, TableRow, Paper, 
    TableHead, TextField, Checkbox, Chip, Card, CardContent, TableSortLabel,
    Alert, Modal, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { useUser } from '../contexts/UserContext';
import Swal from 'sweetalert2';
import { useAccesos } from '../contexts/AccesosContext';

function ConciliacionBancaria() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    // Estados para filtros
    const [bancos, setBancos] = useState([]);
    const [cuentasBancarias, setCuentasBancarias] = useState([]);
    const [idBanco, setIdBanco] = useState('');
    const [idCuentaBancaria, setIdCuentaBancaria] = useState('');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    
    // Estados para movimientos y conciliación
    const [movimientos, setMovimientos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [descripcion, setDescripcion] = useState('');
    const [busquedaMovimiento, setBusquedaMovimiento] = useState('');
    const [filtroEstadoMovimiento, setFiltroEstadoMovimiento] = useState('');
    const [ordenMovimientos, setOrdenMovimientos] = useState({ campo: 'fecha', direccion: 'asc' });
    
    // Estados para lista de conciliaciones guardadas
    const [conciliaciones, setConciliaciones] = useState([]);
    const [loadingConciliaciones, setLoadingConciliaciones] = useState(false);
    const [imprimiendoId, setImprimiendoId] = useState(null);
    const [busquedaConciliacion, setBusquedaConciliacion] = useState('');
    const [filtroBancoLista, setFiltroBancoLista] = useState('');
    const [filtroCuentaLista, setFiltroCuentaLista] = useState('');
    const [filtroEstadoLista, setFiltroEstadoLista] = useState('');
    const [filtroFechaDesdeLista, setFiltroFechaDesdeLista] = useState('');
    const [filtroFechaHastaLista, setFiltroFechaHastaLista] = useState('');
    const [ordenLista, setOrdenLista] = useState({ campo: 'codigo', direccion: 'asc' });
    const [idConciliacionActiva, setIdConciliacionActiva] = useState(null);
    
    // Estados para modal de visualización PDF
    const [openModalPdf, setOpenModalPdf] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [pdfNombre, setPdfNombre] = useState('');

    // Cargar catálogo de bancos y conciliaciones al montar
    useEffect(() => {
        cargarBancos();
        cargarConciliaciones();
    }, []);

    // Limpiar URL del PDF al cerrar modal
    useEffect(() => {
        if (!openModalPdf && pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl('');
        }
    }, [openModalPdf]);

    // Cargar cuentas bancarias cuando cambia el banco
    useEffect(() => {
        if (idBanco) {
            cargarCuentasBancarias(idBanco);
        } else {
            setCuentasBancarias([]);
            setIdCuentaBancaria('');
        }
    }, [idBanco]);

    const cargarBancos = async () => {
        try {
            const response = await fetch(`${server}/ChkBancoes/catalogo`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setBancos(data.$values || data);
            }
        } catch (error) {
            console.error('Error al cargar bancos:', error);
            Swal.fire('Error', 'No se pudieron cargar los bancos', 'error');
        }
    };

    const cargarCuentasBancarias = async (bancoId) => {
        try {
            const response = await fetch(`${server}/ChkBancosCuentasBancarias/banco/${bancoId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setCuentasBancarias(data.$values || data);
            }
        } catch (error) {
            console.error('Error al cargar cuentas bancarias:', error);
            Swal.fire('Error', 'No se pudieron cargar las cuentas bancarias', 'error');
        }
    };

    const cargarConciliaciones = async () => {
        setLoadingConciliaciones(true);
        try {
            const response = await fetch(`${server}/ctb/concilacion-bancaria/all`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setConciliaciones(data.$values || data);
            }
        } catch (error) {
            console.error('Error al cargar conciliaciones:', error);
            Swal.fire('Error', 'No se pudieron cargar las conciliaciones guardadas', 'error');
        } finally {
            setLoadingConciliaciones(false);
        }
    };

    const obtenerDetallesConciliacion = (conciliacion) => {
        const detalles = conciliacion?.ctbConcilacionBancariaDetalles || conciliacion?.CtbConcilacionBancariaDetalles;
        if (!detalles) return [];
        if (Array.isArray(detalles)) return detalles;
        if (Array.isArray(detalles.$values)) return detalles.$values;
        return [];
    };

    const construirTokenRango = (fi, ff) => {
        const [yearIni, monthIni, dayIni] = fi.split('-');
        const [yearFin, monthFin, dayFin] = ff.split('-');
        return `${dayIni}/${monthIni}/${yearIni} - ${dayFin}/${monthFin}/${yearFin}`;
    };

    const buscarConciliacionExistentePorRango = (bancoId, cuentaId, fi, ff) => {
        if (!bancoId || !cuentaId || !fi || !ff) return null;

        const tokenRango = construirTokenRango(fi, ff);
        return conciliaciones.find((conc) => {
            const idBancoConc = Number(conc.idBanco ?? conc.IdBanco ?? conc.chkBancosNavigation?.idBanco ?? 0);
            const idCuentaConc = Number(conc.idCuentaBancaria ?? conc.IdCuentaBancaria ?? conc.idCuentaBancariaNavigation?.idCuentaBancaria ?? 0);
            const descripcionConc = (conc.descripcion || conc.Descripcion || '').toString();

            return idBancoConc === Number(bancoId)
                && idCuentaConc === Number(cuentaId)
                && descripcionConc.includes(tokenRango);
        }) || null;
    };

    const extraerRangoDesdeDescripcion = (descripcionConciliacion) => {
        const texto = (descripcionConciliacion || '').toString();
        const regex = /(\d{2})\/(\d{2})\/(\d{4})\s*-\s*(\d{2})\/(\d{2})\/(\d{4})/;
        const match = texto.match(regex);

        if (!match) return null;

        const fechaInicioParseada = `${match[3]}-${match[2]}-${match[1]}`;
        const fechaFinParseada = `${match[6]}-${match[5]}-${match[4]}`;
        return { fechaInicioParseada, fechaFinParseada };
    };

    const abrirConciliacionExistente = async (idConciliacion) => {
        setLoading(true);
        try {
            const response = await fetch(`${server}/ctb/concilacion-bancaria/${idConciliacion}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.message || 'No se pudo abrir la conciliación');
            }

            const conc = await response.json();
            const detalles = obtenerDetallesConciliacion(conc);

            const movimientosDesdeConciliacion = detalles.map((det) => ({
                id: det.id || det.Id || 0,
                idConciliacionBancaria: det.idConciliacionBancaria || det.IdConciliacionBancaria || conc.id || conc.Id || 0,
                idAsientoContable: det.idAsientoContable || det.IdAsientoContable || 0,
                fechaBancaria: det.fechaBancaria || det.FechaBancaria || '',
                comentario: det.comentario || det.Comentario || '',
                isConciliado: (det.isConciliado ?? det.IsConciliado ?? true),
                ctbAsientosContable: det.ctbAsientosContable || det.CtbAsientosContable || null,
                seleccionado: true
            }));

            setIdBanco(String(conc.idBanco || conc.IdBanco || ''));
            setIdCuentaBancaria(String(conc.idCuentaBancaria || conc.IdCuentaBancaria || ''));
            setDescripcion(conc.descripcion || conc.Descripcion || '');
            setIdConciliacionActiva(conc.id || conc.Id);

            const rango = extraerRangoDesdeDescripcion(conc.descripcion || conc.Descripcion || '');
            if (rango) {
                setFechaInicio(rango.fechaInicioParseada);
                setFechaFin(rango.fechaFinParseada);
                await buscarMovimientos({
                    idBanco: String(conc.idBanco || conc.IdBanco || ''),
                    idCuentaBancaria: String(conc.idCuentaBancaria || conc.IdCuentaBancaria || ''),
                    fechaInicio: rango.fechaInicioParseada,
                    fechaFin: rango.fechaFinParseada
                });
                setDescripcion(conc.descripcion || conc.Descripcion || '');
            } else {
                setMovimientos(movimientosDesdeConciliacion);
            }

            Swal.fire({
                icon: 'info',
                title: 'Conciliación cargada',
                text: `Se abrió la conciliación ${conc.codigo || conc.Codigo || ''} para edición/revisión`,
                timer: 2200,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error al abrir conciliación:', error);
            Swal.fire('Error', error.message || 'No se pudo abrir la conciliación', 'error');
        } finally {
            setLoading(false);
        }
    };

    const eliminarConciliacion = async (idConciliacion) => {
        const confirmacion = await Swal.fire({
            icon: 'warning',
            title: 'Eliminar encabezado de conciliación',
            text: 'Esta acción eliminará solo el encabezado. Los detalles se mantienen hasta eliminarlos explícitamente.',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (!confirmacion.isConfirmed) return;

        try {
            let response = await fetch(`${server}/ctb/concilacion-bancaria/eliminar/${idConciliacion}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            // Fallback para entornos que bloquean/ignoran DELETE pero permiten POST
            if (response.status === 405) {
                response = await fetch(`${server}/ctb/concilacion-bancaria/eliminar/${idConciliacion}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
            }

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.message || 'No se pudo eliminar la conciliación');
            }

            if (Number(idConciliacionActiva) === Number(idConciliacion)) {
                setIdConciliacionActiva(null);
                setMovimientos([]);
            }

            await cargarConciliaciones();

            Swal.fire({
                icon: 'success',
                title: 'Eliminada',
                text: 'El encabezado de la conciliación fue eliminado correctamente',
                timer: 1800,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error al eliminar conciliación:', error);
            Swal.fire('Error', error.message || 'No se pudo eliminar la conciliación', 'error');
        }
    };

    const eliminarDetalleMovimiento = async (idDetalle) => {
        const confirmacion = await Swal.fire({
            icon: 'warning',
            title: 'Quitar conciliación de la línea',
            text: 'Esta acción eliminará el detalle de conciliación de esta línea y volverá a estado pendiente.',
            showCancelButton: true,
            confirmButtonText: 'Sí, quitar conciliación',
            cancelButtonText: 'Cancelar'
        });

        if (!confirmacion.isConfirmed) return;

        try {
            let response = await fetch(`${server}/ctb/concilacion-bancaria/detalle/${idDetalle}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.status === 405) {
                response = await fetch(`${server}/ctb/concilacion-bancaria/detalle/${idDetalle}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
            }

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.message || 'No se pudo quitar la conciliación de la línea');
            }

            await buscarMovimientos();
            await cargarConciliaciones();

            Swal.fire({
                icon: 'success',
                title: 'Conciliación removida',
                text: 'La línea volvió a estado pendiente',
                timer: 1800,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error al quitar conciliación de la línea:', error);
            Swal.fire('Error', error.message || 'No se pudo quitar la conciliación de la línea', 'error');
        }
    };

    const imprimirConciliacion = async (id) => {
        setImprimiendoId(id);
        try {
            const response = await fetch(`${server}/InformesContablesLinux/conciliacion/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': 'application/pdf'
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const urlBlob = window.URL.createObjectURL(blob);
                
                // Configurar y abrir modal con el PDF
                const nombreArchivo = `Conciliacion_${id}_${new Date().toISOString().split('T')[0]}.pdf`;
                setPdfUrl(urlBlob);
                setPdfNombre(nombreArchivo);
                setOpenModalPdf(true);
            } else {
                const errorData = await response.json().catch(() => ({}));
                Swal.fire('Error', errorData.message || 'No se pudo generar el reporte', 'error');
            }
        } catch (error) {
            console.error('Error al imprimir:', error);
            Swal.fire('Error', 'Error al generar el reporte de conciliación', 'error');
        } finally {
            setImprimiendoId(null);
        }
    };

    // Cerrar modal PDF
    const handleCloseModalPdf = () => {
        setOpenModalPdf(false);
    };

    // Descargar PDF
    const handleDownloadPdf = () => {
        if (pdfUrl && pdfNombre) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = pdfNombre;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            Swal.fire({
                icon: 'success',
                title: 'Descargado',
                text: 'El PDF se ha descargado correctamente',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };

    const buscarMovimientos = async (filtros = null) => {
        const bancoFiltro = filtros?.idBanco ?? idBanco;
        const cuentaFiltro = filtros?.idCuentaBancaria ?? idCuentaBancaria;
        const fechaInicioFiltro = filtros?.fechaInicio ?? fechaInicio;
        const fechaFinFiltro = filtros?.fechaFin ?? fechaFin;

        if (!bancoFiltro || !cuentaFiltro || !fechaInicioFiltro || !fechaFinFiltro) {
            Swal.fire('Validación', 'Todos los filtros son obligatorios', 'warning');
            return;
        }

        if (fechaInicioFiltro > fechaFinFiltro) {
            Swal.fire('Validación', 'La fecha de inicio no puede ser mayor a la fecha fin', 'warning');
            return;
        }

        setLoading(true);
        try {
            // Convertir fechas de yyyy-MM-dd a yyyyMMdd (formato requerido por el backend)
            const fechaInicioFormateada = fechaInicioFiltro.replace(/-/g, '');
            const fechaFinFormateada = fechaFinFiltro.replace(/-/g, '');
            
            const response = await fetch(
                `${server}/ctb/concilacion-bancaria/movimientos/${cuentaFiltro}/${bancoFiltro}/${fechaInicioFormateada}/${fechaFinFormateada}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                const movimientosCrudos = data.$values || data;
                
                const movimientosConEstado = movimientosCrudos.map(mov => ({
                    // Mapeo explícito para asegurar compatibilidad PascalCase/camelCase
                    id: mov.id || mov.Id,
                    idConciliacionBancaria: mov.idConciliacionBancaria || mov.IdConciliacionBancaria || 0,
                    idAsientoContable: mov.idAsientoContable || mov.IdAsientoContable || 0,
                    fechaBancaria: mov.fechaBancaria || mov.FechaBancaria || '',
                    comentario: mov.comentario || mov.Comentario || '',
                    isConciliado: mov.isConciliado || mov.IsConciliado || false,
                    // Mantener todos los demás campos originales
                    ...mov,
                    // Estados del frontend
                    seleccionado: false
                }));
                
                setMovimientos(movimientosConEstado);
                
                // Generar descripción automática
                const bancoSeleccionado = bancos.find(b => b.idBanco === parseInt(bancoFiltro));
                const cuentaSeleccionada = cuentasBancarias.find(c => c.idCuentaBancaria === parseInt(cuentaFiltro));
                const [yearIni, monthIni, dayIni] = fechaInicioFiltro.split('-');
                const [yearFin, monthFin, dayFin] = fechaFinFiltro.split('-');
                const descGen = `Conciliación ${bancoSeleccionado?.nombreBanco || ''} - ${cuentaSeleccionada?.cuentaBancaria || ''} - ${dayIni}/${monthIni}/${yearIni} - ${dayFin}/${monthFin}/${yearFin}`;
                setDescripcion(descGen);

                const conciliacionExistente = buscarConciliacionExistentePorRango(bancoFiltro, cuentaFiltro, fechaInicioFiltro, fechaFinFiltro);
                if (conciliacionExistente) {
                    setIdConciliacionActiva(conciliacionExistente.id || conciliacionExistente.Id);

                    const idsDetalleExistente = new Set(
                        obtenerDetallesConciliacion(conciliacionExistente)
                            .map((det) => Number(det.idAsientoContable || det.IdAsientoContable || 0))
                            .filter((id) => id > 0)
                    );

                    const movimientosConSeleccion = movimientosConEstado.map((mov) => ({
                        ...mov,
                        seleccionado: idsDetalleExistente.has(Number(mov.idAsientoContable || 0))
                    }));

                    setMovimientos(movimientosConSeleccion);
                } else {
                    setIdConciliacionActiva(null);
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Error del servidor:', errorData);
                Swal.fire('Error', errorData.message || 'No se pudieron cargar los movimientos', 'error');
            }
        } catch (error) {
            console.error('Error al buscar movimientos:', error);
            Swal.fire('Error', 'Error al buscar movimientos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSeleccionarMovimiento = (index) => {
        const nuevosMovimientos = [...movimientos];
        const movimiento = nuevosMovimientos[index];
        
        // No permitir seleccionar si ya está conciliado
        if (movimiento.isConciliado) {
            return;
        }

        movimiento.seleccionado = !movimiento.seleccionado;
        setMovimientos(nuevosMovimientos);
    };

    const handleFechaBancariaChange = (index, fecha) => {
        const nuevosMovimientos = [...movimientos];
        nuevosMovimientos[index].fechaBancaria = fecha;
        setMovimientos(nuevosMovimientos);
    };

    const handleComentarioChange = (index, comentario) => {
        const nuevosMovimientos = [...movimientos];
        nuevosMovimientos[index].comentario = comentario;
        setMovimientos(nuevosMovimientos);
    };

    // Cálculos según especificaciones del backend
    // Total Valor Contable: Suma del Debe - Suma Haber de TODA la lista
    const calcularSaldoContable = () => {
        const totalDebe = movimientos.reduce((sum, mov) => sum + (mov.ctbAsientosContable?.debe || 0), 0);
        const totalHaber = movimientos.reduce((sum, mov) => sum + (mov.ctbAsientosContable?.haber || 0), 0);
        return totalDebe - totalHaber;
    };

    // Total Valor Bancario: Suma del debe - suma haber de lo CONCILIADO (seleccionado)
    const calcularSaldoBancario = () => {
        const movimientosSeleccionados = movimientos.filter(mov => mov.seleccionado || mov.isConciliado);
        const totalDebe = movimientosSeleccionados.reduce((sum, mov) => sum + (mov.ctbAsientosContable?.debe || 0), 0);
        const totalHaber = movimientosSeleccionados.reduce((sum, mov) => sum + (mov.ctbAsientosContable?.haber || 0), 0);
        return totalDebe - totalHaber;
    };

    // Diferencia: Total Contable - Total Bancario
    const calcularDiferencia = () => {
        const saldoCont = calcularSaldoContable();
        const saldoBanc = calcularSaldoBancario();
        return saldoCont - saldoBanc;
    };

    const validarConciliacion = () => {
        const movimientosSeleccionados = movimientos.filter(mov => mov.seleccionado);
        
        if (movimientosSeleccionados.length === 0) {
            Swal.fire('Validación', 'Debe seleccionar al menos un movimiento', 'warning');
            return false;
        }

        // Validar que todos los movimientos seleccionados tengan fecha bancaria
        const sinFecha = movimientosSeleccionados.filter(mov => !mov.fechaBancaria);
        if (sinFecha.length > 0) {
            Swal.fire('Validación', 'Todos los movimientos seleccionados deben tener una fecha de estado de cuenta', 'warning');
            return false;
        }

        // Advertir si la diferencia es mayor al 5%
        const diferencia = Math.abs(calcularDiferencia());
        const saldoCont = Math.abs(calcularSaldoContable());
        if (saldoCont > 0 && (diferencia / saldoCont) > 0.05) {
            return Swal.fire({
                title: 'Diferencia Significativa',
                text: `La diferencia es mayor al 5% del saldo contable. ¿Desea continuar?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, continuar',
                cancelButtonText: 'Cancelar'
            });
        }

        return Promise.resolve({ isConfirmed: true });
    };

    const guardarConciliacion = async () => {
        const validacion = await validarConciliacion();
        if (!validacion || !validacion.isConfirmed) {
            return;
        }

        const movimientosSeleccionados = movimientos.filter(mov => mov.seleccionado || mov.isConciliado);

        const conciliacionExistente = idConciliacionActiva
            ? { id: idConciliacionActiva }
            : buscarConciliacionExistentePorRango(idBanco, idCuentaBancaria, fechaInicio, fechaFin);

        const idConciliacionObjetivo = conciliacionExistente?.id || conciliacionExistente?.Id || null;
        setLoading(true);

        try {
            const saldoCont = calcularSaldoContable();
            const saldoBanc = calcularSaldoBancario();
            const dif = calcularDiferencia();

            // Preparar el modelo de conciliación completo
            const usuarioPadded = (user.codigoUsuario || '').padEnd(20, ' ');
            
            const conciliacion = {
                id: idConciliacionObjetivo || undefined,
                idBanco: parseInt(idBanco),
                idCuentaBancaria: parseInt(idCuentaBancaria),
                fechaInicio: fechaInicio,
                fechaFin: fechaFin,
                descripcion: descripcion,
                saldoContable: saldoCont,
                saldoBancario: saldoBanc,
                diferencia: dif,
                codigoUsuario: user.codigoUsuario,
                ingresadoPor: usuarioPadded,
                modificadoPor: usuarioPadded,
                ctbConcilacionBancariaDetalles: movimientosSeleccionados.map(mov => ({
                    id: mov.id > 0 ? mov.id : undefined,
                    idAsientoContable: mov.idAsientoContable,
                    fechaBancaria: mov.fechaBancaria,
                    comentario: mov.comentario || null,
                    isConciliado: true
                }))
            };

            const esActualizacion = !!idConciliacionObjetivo;

            const response = await fetch(esActualizacion
                ? `${server}/ctb/concilacion-bancaria/${idConciliacionObjetivo}`
                : `${server}/ctb/concilacion-bancaria/conciliar`, {
                method: esActualizacion ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(conciliacion)
            });

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: esActualizacion ? 'Conciliación Actualizada' : 'Conciliación Guardada',
                    text: `Se conciliaron ${movimientosSeleccionados.length} movimientos`,
                    timer: 2000
                });

                const dataGuardado = await response.json().catch(() => ({}));
                const idGuardado = dataGuardado?.data?.id || dataGuardado?.data?.Id || idConciliacionObjetivo;
                if (idGuardado) setIdConciliacionActiva(idGuardado);
                
                // Refrescar las tablas
                buscarMovimientos();
                cargarConciliaciones();
            } else {
                const errorData = await response.json().catch(() => ({}));
                Swal.fire('Error', errorData.message || 'Hubo un error al guardar la conciliación', 'error');
            }
        } catch (error) {
            console.error('Error al guardar conciliación:', error);
            Swal.fire('Error', 'Error al guardar la conciliación', 'error');
        } finally {
            setLoading(false);
        }
    };

    const saldoContable = calcularSaldoContable();
    const saldoBancarioCalculado = calcularSaldoBancario();
    const diferencia = calcularDiferencia();
    const movimientosSeleccionados = movimientos.filter(mov => mov.seleccionado).length;

    const cuentaSeleccionada = cuentasBancarias.find(
        c => c.idCuentaBancaria === Number(idCuentaBancaria)
    );

    const simboloMoneda = cuentaSeleccionada?.simboloMoneda + ' ' || '';

        const formatearMonto = (valor) => {
        return Number(valor).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const normalizarFechaSolo = (valor) => {
        if (!valor) return '';

        const texto = String(valor).trim();
        const matchIso = texto.match(/^(\d{4}-\d{2}-\d{2})/);
        if (matchIso) return matchIso[1];

        const matchLatino = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (matchLatino) return `${matchLatino[3]}-${matchLatino[2]}-${matchLatino[1]}`;

        return '';
    };

    const formatearFechaSolo = (valor) => {
        const iso = normalizarFechaSolo(valor);
        if (!iso) return '';

        const [year, month, day] = iso.split('-');
        return `${day}/${month}/${year}`;
    };

    const obtenerSimboloMonedaConciliacion = (conc) => {
        return (
            conc?.idCuentaBancariaNavigation?.idMonedaNavigation?.simbolo ||
            conc?.idCuentaBancariaNavigation?.idMonedaNavigation?.Simbolo ||
            conc?.IdCuentaBancariaNavigation?.IdMonedaNavigation?.simbolo ||
            conc?.IdCuentaBancariaNavigation?.IdMonedaNavigation?.Simbolo ||
            'L'
        );
    };

    const obtenerTextoBancoConciliacion = (conc) => {
        return conc?.chkBancosNavigation?.nombreBanco || conc?.ChkBancosNavigation?.nombreBanco || '';
    };

    const obtenerTextoCuentaConciliacion = (conc) => {
        return conc?.idCuentaBancariaNavigation?.cuentaBancaria
            || conc?.IdCuentaBancariaNavigation?.cuentaBancaria
            || '';
    };

    const obtenerDetalleCuentaContableConciliacion = (conc) => {
        const cuentaNav = conc?.idCuentaBancariaNavigation || conc?.IdCuentaBancariaNavigation || null;
        if (!cuentaNav) return '';

        const navCuentaContable = cuentaNav?.idCuentaContableNewNavigation
            || cuentaNav?.IdCuentaContableNewNavigation
            || null;

        const codigoCuenta = navCuentaContable?.codigo
            || navCuentaContable?.Codigo
            || navCuentaContable?.codigoCuenta
            || navCuentaContable?.CodigoCuenta
            || '';

        const descripcionCuenta = navCuentaContable?.descripcion
            || navCuentaContable?.Descripcion
            || navCuentaContable?.nombreCuenta
            || navCuentaContable?.NombreCuenta
            || '';

        if (codigoCuenta && descripcionCuenta) {
            return `${codigoCuenta} - ${descripcionCuenta}`;
        }

        return (
            descripcionCuenta
            || codigoCuenta
            || cuentaNav?.cuentaContableNew
            || cuentaNav?.CuentaContableNew
            || cuentaNav?.cuentaContable
            || cuentaNav?.CuentaContable
            || (cuentaNav?.idCuentaContableNew ? `ID ${cuentaNav.idCuentaContableNew}` : '')
            || (cuentaNav?.IdCuentaContableNew ? `ID ${cuentaNav.IdCuentaContableNew}` : '')
            || ''
        );
    };

    const obtenerEstadoConciliacion = (conc) => {
        return Number(conc?.diferencia || 0) === 0 ? 'Cuadrada' : 'Pendiente';
    };

    const obtenerFechaConciliacion = (conc) => {
        if (!conc?.fechaIngreso) return null;
        const fecha = new Date(conc.fechaIngreso);
        return Number.isNaN(fecha.getTime()) ? null : fecha;
    };

    const limpiarFiltrosLista = () => {
        setBusquedaConciliacion('');
        setFiltroBancoLista('');
        setFiltroCuentaLista('');
        setFiltroEstadoLista('');
        setFiltroFechaDesdeLista('');
        setFiltroFechaHastaLista('');
    };

    const ordenarLista = (campo) => {
        setOrdenLista((prev) => {
            if (prev.campo === campo) {
                return {
                    campo,
                    direccion: prev.direccion === 'asc' ? 'desc' : 'asc'
                };
            }
            return { campo, direccion: 'asc' };
        });
    };

    const obtenerValorOrdenConciliacion = (conc, campo) => {
        switch (campo) {
            case 'id':
                return Number(conc?.id || 0);
            case 'codigo':
                return (conc?.codigo || '').toString().toLowerCase();
            case 'banco':
                return obtenerTextoBancoConciliacion(conc).toLowerCase();
            case 'cuenta':
                return obtenerTextoCuentaConciliacion(conc).toLowerCase();
            case 'descripcion':
                return (conc?.descripcion || '').toString().toLowerCase();
            case 'fechaIngreso': {
                const fecha = obtenerFechaConciliacion(conc);
                return fecha ? fecha.getTime() : 0;
            }
            case 'saldoContable':
                return Number(conc?.saldoContable || 0);
            case 'saldoBancario':
                return Number(conc?.saldoBancario || 0);
            case 'diferencia':
                return Number(conc?.diferencia || 0);
            case 'estado':
                return obtenerEstadoConciliacion(conc).toLowerCase();
            default:
                return '';
        }
    };

    const bancosFiltroLista = [...new Set(conciliaciones.map(obtenerTextoBancoConciliacion).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const cuentasFiltroLista = Array.from(
        new Map(
            conciliaciones
                .map((conc) => ({
                    cuenta: obtenerTextoCuentaConciliacion(conc),
                    detalle: obtenerDetalleCuentaContableConciliacion(conc)
                }))
                .filter((item) => item.cuenta)
                .map((item) => [`${item.cuenta}|${item.detalle}`, item])
        ).values()
    ).sort((a, b) => a.cuenta.localeCompare(b.cuenta));

    const conciliacionesFiltradas = conciliaciones.filter((conc) => {
        const query = busquedaConciliacion.trim().toLowerCase();
        const banco = obtenerTextoBancoConciliacion(conc);
        const cuenta = obtenerTextoCuentaConciliacion(conc);
        const descripcionConc = (conc?.descripcion || '').toString();
        const codigo = (conc?.codigo || '').toString();
        const estado = obtenerEstadoConciliacion(conc);
        const fecha = obtenerFechaConciliacion(conc);

        const cumpleBusqueda = !query || [codigo, banco, cuenta, descripcionConc]
            .some((texto) => (texto || '').toString().toLowerCase().includes(query));

        const cumpleBanco = !filtroBancoLista || banco === filtroBancoLista;
        const cumpleCuenta = !filtroCuentaLista || cuenta === filtroCuentaLista;
        const cumpleEstado = !filtroEstadoLista || estado === filtroEstadoLista;

        const cumpleFechaDesde = !filtroFechaDesdeLista || (fecha && fecha >= new Date(`${filtroFechaDesdeLista}T00:00:00`));
        const cumpleFechaHasta = !filtroFechaHastaLista || (fecha && fecha <= new Date(`${filtroFechaHastaLista}T23:59:59`));

        return cumpleBusqueda && cumpleBanco && cumpleCuenta && cumpleEstado && cumpleFechaDesde && cumpleFechaHasta;
    });

    const conciliacionesFiltradasOrdenadas = [...conciliacionesFiltradas].sort((a, b) => {
        const valorA = obtenerValorOrdenConciliacion(a, ordenLista.campo);
        const valorB = obtenerValorOrdenConciliacion(b, ordenLista.campo);

        if (valorA < valorB) return ordenLista.direccion === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenLista.direccion === 'asc' ? 1 : -1;
        return 0;
    });

    const exportarConciliacionesExcel = () => {
        if (conciliacionesFiltradasOrdenadas.length === 0) {
            Swal.fire('Exportación', 'No hay datos para exportar con los filtros actuales.', 'warning');
            return;
        }

        const rows = conciliacionesFiltradasOrdenadas.map((conc) => ({
            ID: conc.id || '',
            Codigo: conc.codigo || 'N/A',
            Banco: obtenerTextoBancoConciliacion(conc) || 'N/A',
            CuentaBancaria: obtenerTextoCuentaConciliacion(conc) || 'N/A',
            DetalleCuentaContable: obtenerDetalleCuentaContableConciliacion(conc) || '',
            Descripcion: conc.descripcion || '',
            FechaEmision: conc.fechaIngreso ? format(new Date(conc.fechaIngreso), 'dd/MM/yyyy') : '',
            Moneda: obtenerSimboloMonedaConciliacion(conc),
            SaldoContable: Number(conc.saldoContable || 0),
            SaldoBancario: Number(conc.saldoBancario || 0),
            Diferencia: Number(conc.diferencia || 0),
            Estado: obtenerEstadoConciliacion(conc)
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Conciliaciones');
        XLSX.writeFile(workbook, `conciliaciones_bancarias_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
    };

    const ordenarTablaMovimientos = (campo) => {
        setOrdenMovimientos((prev) => {
            if (prev.campo === campo) {
                return {
                    campo,
                    direccion: prev.direccion === 'asc' ? 'desc' : 'asc'
                };
            }
            return { campo, direccion: 'asc' };
        });
    };

    const obtenerFechaPartidaMovimiento = (mov) => {
        const fecha = mov?.ctbAsientosContable?.idPartidaContableNavigation?.fechaPartida;
        if (!fecha) return null;
        const parsed = new Date(fecha);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const obtenerValorOrdenMovimiento = (mov, campo) => {
        switch (campo) {
            case 'fecha': {
                const fecha = obtenerFechaPartidaMovimiento(mov);
                return fecha ? fecha.getTime() : 0;
            }
            case 'referencia':
                return (mov?.ctbAsientosContable?.idPartidaContableNavigation?.secuencialManual || '').toString().toLowerCase();
            case 'descripcion':
                return (mov?.ctbAsientosContable?.concepto || '').toString().toLowerCase();
            case 'debe':
                return Number(mov?.ctbAsientosContable?.debe || 0);
            case 'haber':
                return Number(mov?.ctbAsientosContable?.haber || 0);
            case 'fechaBancaria': {
                const iso = normalizarFechaSolo(mov?.fechaBancaria);
                return iso ? Number(iso.replace(/-/g, '')) : 0;
            }
            case 'comentario':
                return (mov?.comentario || '').toString().toLowerCase();
            case 'estado':
                return mov?.isConciliado ? 'conciliado' : 'pendiente';
            default:
                return '';
        }
    };

    const esMovimientoConMontosCero = (mov) => {
        const debe = Number(mov?.ctbAsientosContable?.debe || 0);
        const haber = Number(mov?.ctbAsientosContable?.haber || 0);
        return debe === 0 && haber === 0;
    };

    const movimientosFiltradosOrdenados = [...movimientos.map((mov, originalIndex) => ({ mov, originalIndex }))]
        .filter(({ mov }) => {
            if (esMovimientoConMontosCero(mov)) {
                return false;
            }

            const query = busquedaMovimiento.trim().toLowerCase();
            const referencia = (mov?.ctbAsientosContable?.idPartidaContableNavigation?.secuencialManual || '').toString();
            const concepto = (mov?.ctbAsientosContable?.concepto || '').toString();
            const comentario = (mov?.comentario || '').toString();

            const cumpleBusqueda = !query || [referencia, concepto, comentario]
                .some((texto) => texto.toLowerCase().includes(query));

            const cumpleEstado = !filtroEstadoMovimiento
                || (filtroEstadoMovimiento === 'Conciliado' && mov.isConciliado)
                || (filtroEstadoMovimiento === 'Pendiente' && !mov.isConciliado)
                || (filtroEstadoMovimiento === 'Seleccionado' && mov.seleccionado);

            return cumpleBusqueda && cumpleEstado;
        })
        .sort((a, b) => {
            const valorA = obtenerValorOrdenMovimiento(a.mov, ordenMovimientos.campo);
            const valorB = obtenerValorOrdenMovimiento(b.mov, ordenMovimientos.campo);

            if (valorA < valorB) return ordenMovimientos.direccion === 'asc' ? -1 : 1;
            if (valorA > valorB) return ordenMovimientos.direccion === 'asc' ? 1 : -1;
            return 0;
        });

    const exportarMovimientosExcel = () => {
        if (movimientosFiltradosOrdenados.length === 0) {
            Swal.fire('Exportación', 'No hay movimientos para exportar con los filtros actuales.', 'warning');
            return;
        }

        const rows = movimientosFiltradosOrdenados.map(({ mov }) => ({
            Referencia: mov?.ctbAsientosContable?.idPartidaContableNavigation?.secuencialManual || '',
            Fecha: mov?.ctbAsientosContable?.idPartidaContableNavigation?.fechaPartida
                ? format(new Date(mov.ctbAsientosContable.idPartidaContableNavigation.fechaPartida), 'dd/MM/yyyy')
                : '',
            Descripcion: mov?.ctbAsientosContable?.concepto || '',
            Debe: Number(mov?.ctbAsientosContable?.debe || 0),
            Haber: Number(mov?.ctbAsientosContable?.haber || 0),
            FechaEstadoCuenta: formatearFechaSolo(mov?.fechaBancaria),
            Comentario: mov?.comentario || '',
            Estado: mov?.isConciliado ? 'Conciliado' : 'Pendiente',
            Seleccionado: mov?.seleccionado ? 'Si' : 'No'
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimientos');
        XLSX.writeFile(workbook, `movimientos_conciliacion_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
    };

    return (
        <div className="right_col" role="main">
            <div className="page-title">
                <div className="title_left">
                    <h3>Conciliación Bancaria</h3>
                </div>
            </div>

            <div className="clearfix"></div>

            <div className="row">
                <div className="col-md-12 col-sm-12">
                    <div className="x_panel">
                        <div className="x_title">
                            <h2>Filtros de Búsqueda</h2>
                            <div className="clearfix"></div>
                        </div>
                        <div className="x_content">
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Banco *</InputLabel>
                                        <Select
                                            value={idBanco}
                                            onChange={(e) => setIdBanco(e.target.value)}
                                            label="Banco *"
                                        >
                                            <MenuItem value="">Seleccionar</MenuItem>
                                            {bancos.map((banco) => (
                                                <MenuItem key={banco.idBanco} value={banco.idBanco}>
                                                    {banco.nombreBanco}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Cuenta Bancaria *</InputLabel>
                                        <Select
                                            value={idCuentaBancaria}
                                            onChange={(e) => setIdCuentaBancaria(e.target.value)}
                                            label="Cuenta Bancaria *"
                                            disabled={!idBanco}
                                        >
                                            <MenuItem value="">Seleccionar</MenuItem>
                                            {cuentasBancarias.map((cuenta) => (
                                                <MenuItem key={cuenta.idCuentaBancaria} value={cuenta.idCuentaBancaria}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                                                        <Typography variant="body2">
                                                            {cuenta.descripcionCompleta}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            {cuenta.cuentaContableNew || cuenta.CuentaContableNew || 'Sin cuenta contable nueva'}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField
                                        label="Fecha Inicio *"
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={fechaInicio}
                                        onChange={(e) => setFechaInicio(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField
                                        label="Fecha Fin *"
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={fechaFin}
                                        onChange={(e) => setFechaFin(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={buscarMovimientos}
                                        disabled={loading || !idBanco || !idCuentaBancaria || !fechaInicio || !fechaFin}
                                    >
                                        {loading ? 'Buscando...' : 'Buscar'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </div>
                    </div>
                </div>
            </div>

            {movimientos.length > 0 && (
                <>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="x_panel">
                                <div className="x_content">
                                    {idConciliacionActiva && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            Editando conciliación existente ID: <strong>{idConciliacionActiva}</strong>
                                        </Alert>
                                    )}
                                    <TextField
                                        label="Descripción de Conciliación"
                                        fullWidth
                                        value={descripcion}
                                        onChange={(e) => setDescripcion(e.target.value)}
                                        size="small"
                                        helperText="Edite la descripción si es necesario"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <Alert severity="info" sx={{ mb: 2 }}>
                                ℹ️ Es normal que no se llegue a conciliar al 100% en este momento. Las diferencias pueden deberse a movimientos en tránsito, cheques no cobrados o débitos pendientes.
                            </Alert>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-9 col-sm-12">
                            <div className="x_panel">
                                <div className="x_title">
                                    <h2>Movimientos Contables</h2>
                                    <div className="clearfix"></div>
                                </div>
                                <div className="x_content">
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Buscar en movimientos"
                                                size="small"
                                                fullWidth
                                                value={busquedaMovimiento}
                                                onChange={(e) => setBusquedaMovimiento(e.target.value)}
                                                placeholder="Buscar por referencia, descripción o comentario"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Estado</InputLabel>
                                                <Select
                                                    value={filtroEstadoMovimiento}
                                                    onChange={(e) => setFiltroEstadoMovimiento(e.target.value)}
                                                    label="Estado"
                                                >
                                                    <MenuItem value="">Todos</MenuItem>
                                                    <MenuItem value="Conciliado">Conciliado</MenuItem>
                                                    <MenuItem value="Pendiente">Pendiente</MenuItem>
                                                    <MenuItem value="Seleccionado">Seleccionado</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                fullWidth
                                                onClick={exportarMovimientosExcel}
                                            >
                                                Exportar Movimientos
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <TableContainer component={Paper} style={{ maxHeight: 500 }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell padding="checkbox" style={{ width: 50 }}>Sel.</TableCell>
                                                    <TableCell>
                                                        <TableSortLabel
                                                            active={ordenMovimientos.campo === 'fecha'}
                                                            direction={ordenMovimientos.campo === 'fecha' ? ordenMovimientos.direccion : 'asc'}
                                                            onClick={() => ordenarTablaMovimientos('fecha')}
                                                        >
                                                            Fecha
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TableSortLabel
                                                            active={ordenMovimientos.campo === 'referencia'}
                                                            direction={ordenMovimientos.campo === 'referencia' ? ordenMovimientos.direccion : 'asc'}
                                                            onClick={() => ordenarTablaMovimientos('referencia')}
                                                        >
                                                            Referencia
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TableSortLabel
                                                            active={ordenMovimientos.campo === 'descripcion'}
                                                            direction={ordenMovimientos.campo === 'descripcion' ? ordenMovimientos.direccion : 'asc'}
                                                            onClick={() => ordenarTablaMovimientos('descripcion')}
                                                        >
                                                            Descripción
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <TableSortLabel
                                                            active={ordenMovimientos.campo === 'debe'}
                                                            direction={ordenMovimientos.campo === 'debe' ? ordenMovimientos.direccion : 'asc'}
                                                            onClick={() => ordenarTablaMovimientos('debe')}
                                                        >
                                                            Debe
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <TableSortLabel
                                                            active={ordenMovimientos.campo === 'haber'}
                                                            direction={ordenMovimientos.campo === 'haber' ? ordenMovimientos.direccion : 'asc'}
                                                            onClick={() => ordenarTablaMovimientos('haber')}
                                                        >
                                                            Haber
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell style={{ width: 150 }}>
                                                        <TableSortLabel
                                                            active={ordenMovimientos.campo === 'fechaBancaria'}
                                                            direction={ordenMovimientos.campo === 'fechaBancaria' ? ordenMovimientos.direccion : 'asc'}
                                                            onClick={() => ordenarTablaMovimientos('fechaBancaria')}
                                                        >
                                                            Fecha Estado Cuenta
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell style={{ width: 150 }}>
                                                        <TableSortLabel
                                                            active={ordenMovimientos.campo === 'comentario'}
                                                            direction={ordenMovimientos.campo === 'comentario' ? ordenMovimientos.direccion : 'asc'}
                                                            onClick={() => ordenarTablaMovimientos('comentario')}
                                                        >
                                                            Comentario
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TableSortLabel
                                                            active={ordenMovimientos.campo === 'estado'}
                                                            direction={ordenMovimientos.campo === 'estado' ? ordenMovimientos.direccion : 'asc'}
                                                            onClick={() => ordenarTablaMovimientos('estado')}
                                                        >
                                                            Estado
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell style={{ width: 130 }}>Acciones</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {movimientosFiltradosOrdenados.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={10} align="center">
                                                            <Typography variant="body2" color="textSecondary">
                                                                No hay movimientos para los filtros actuales
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {movimientosFiltradosOrdenados.map(({ mov, originalIndex }) => (
                                                    <TableRow 
                                                        key={`${mov.idAsientoContable || 'mov'}-${originalIndex}`}
                                                        style={{ 
                                                            backgroundColor: mov.isConciliado ? '#f5f5f5' : mov.seleccionado ? '#e3f2fd' : 'white'
                                                        }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={mov.seleccionado}
                                                                onChange={() => handleSeleccionarMovimiento(originalIndex)}
                                                                disabled={mov.isConciliado}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {mov.ctbAsientosContable?.idPartidaContableNavigation?.fechaPartida ? 
                                                                format(new Date(mov.ctbAsientosContable.idPartidaContableNavigation.fechaPartida), 'dd/MM/yyyy') : ''}
                                                        </TableCell>
                                                        <TableCell>{mov.ctbAsientosContable?.idPartidaContableNavigation?.secuencialManual || ''}</TableCell>
                                                        <TableCell>{mov.ctbAsientosContable?.concepto || ''}</TableCell>
                                                        <TableCell align="right">{formatearMonto(mov.ctbAsientosContable?.debe || 0)}</TableCell>
                                                        <TableCell align="right">{formatearMonto(mov.ctbAsientosContable?.haber || 0)}</TableCell>
                                                        <TableCell>
                                                            {!mov.isConciliado && (
                                                                <TextField
                                                                    type="date"
                                                                    size="small"
                                                                    fullWidth
                                                                    value={normalizarFechaSolo(mov.fechaBancaria)}
                                                                    onChange={(e) => handleFechaBancariaChange(originalIndex, e.target.value)}
                                                                    disabled={!mov.seleccionado}
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            )}
                                                            {mov.isConciliado && mov.fechaBancaria && (
                                                                <Typography variant="body2">
                                                                    {formatearFechaSolo(mov.fechaBancaria)}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                value={mov.comentario}
                                                                onChange={(e) => handleComentarioChange(originalIndex, e.target.value)}
                                                                disabled={mov.isConciliado || !mov.seleccionado}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {mov.isConciliado ? (
                                                                <Chip label="Conciliado" size="small" color="success" />
                                                            ) : (
                                                                <Chip label="Pendiente" size="small" color="default" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {mov.isConciliado && Number(mov.id || 0) > 0 && (
                                                                <Button
                                                                    variant="outlined"
                                                                    color="warning"
                                                                    size="small"
                                                                    onClick={() => eliminarDetalleMovimiento(mov.id)}
                                                                >
                                                                    Quitar Conc.
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-3 col-sm-12">
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Resumen de Conciliación
                                    </Typography>
                                    
                                    <Box sx={{ mt: 2 }}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Box sx={{ 
                                                    p: 1, 
                                                    bgcolor: '#f5f5f5', 
                                                    borderRadius: 1
                                                }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Total Valor Contable:
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {simboloMoneda}{formatearMonto(saldoContable)}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Box sx={{ 
                                                    p: 1, 
                                                    bgcolor: '#e3f2fd', 
                                                    borderRadius: 1
                                                }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Total Valor Bancaria (Conciliado):
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {simboloMoneda}{formatearMonto(saldoBancarioCalculado)}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Box sx={{ 
                                                    p: 1, 
                                                    bgcolor: diferencia === 0 ? '#e8f5e9' : '#fff3e0', 
                                                    borderRadius: 1
                                                }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Dif.:
                                                    </Typography>
                                                    <Typography 
                                                        variant="h6"
                                                        color={diferencia === 0 ? 'success.main' : 'warning.main'}
                                                    >
                                                        {simboloMoneda}{formatearMonto(diferencia)}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Box sx={{ 
                                                    p: 1, 
                                                    bgcolor: '#e3f2fd', 
                                                    borderRadius: 1,
                                                    mt: 1
                                                }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Movimientos Seleccionados:
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {movimientosSeleccionados}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sx={{ mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    fullWidth
                                                    onClick={guardarConciliacion}
                                                    disabled={loading || movimientosSeleccionados === 0 || !tienePermiso('Conciliar Transacción')}
                                                >
                                                    Guardar Conciliación
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </>
            )}

            {movimientos.length === 0 && !loading && (
                <div className="row">
                    <div className="col-md-12">
                        <Alert severity="info">
                            Seleccione los filtros y haga clic en "Buscar" para cargar los movimientos contables.
                        </Alert>
                    </div>
                </div>
            )}

            {/* Lista de Conciliaciones Guardadas */}
            <div className="row" style={{ marginTop: 40 }}>
                <div className="col-md-12">
                    <div className="x_panel">
                        <div className="x_title">
                            <h2>Conciliaciones Bancarias Guardadas</h2>
                            <div className="clearfix"></div>
                        </div>
                        <div className="x_content">
                            {/* Filtros y acciones */}
                            <Grid container spacing={2} style={{ marginBottom: 20 }}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Buscar conciliaciones"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={busquedaConciliacion}
                                        onChange={(e) => setBusquedaConciliacion(e.target.value)}
                                        placeholder="Buscar por código, banco, cuenta, descripción..."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Banco</InputLabel>
                                        <Select
                                            value={filtroBancoLista}
                                            onChange={(e) => setFiltroBancoLista(e.target.value)}
                                            label="Banco"
                                        >
                                            <MenuItem value="">Todos</MenuItem>
                                            {bancosFiltroLista.map((banco) => (
                                                <MenuItem key={banco} value={banco}>{banco}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Cuenta</InputLabel>
                                        <Select
                                            value={filtroCuentaLista}
                                            onChange={(e) => setFiltroCuentaLista(e.target.value)}
                                            label="Cuenta"
                                        >
                                            <MenuItem value="">Todas</MenuItem>
                                            {cuentasFiltroLista.map((item) => (
                                                <MenuItem key={`${item.cuenta}-${item.detalle || 'sin-detalle'}`} value={item.cuenta}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                                                        <Typography variant="body2">{item.cuenta}</Typography>
                                                        {item.detalle && (
                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                {item.detalle}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Estado</InputLabel>
                                        <Select
                                            value={filtroEstadoLista}
                                            onChange={(e) => setFiltroEstadoLista(e.target.value)}
                                            label="Estado"
                                        >
                                            <MenuItem value="">Todos</MenuItem>
                                            <MenuItem value="Cuadrada">Cuadrada</MenuItem>
                                            <MenuItem value="Pendiente">Pendiente</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={1}>
                                    <TextField
                                        label="Desde"
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={filtroFechaDesdeLista}
                                        onChange={(e) => setFiltroFechaDesdeLista(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={1}>
                                    <TextField
                                        label="Hasta"
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={filtroFechaHastaLista}
                                        onChange={(e) => setFiltroFechaHastaLista(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={limpiarFiltrosLista}
                                    >
                                        Limpiar Filtros
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        fullWidth
                                        onClick={exportarConciliacionesExcel}
                                    >
                                        Exportar Excel
                                    </Button>
                                </Grid>
                            </Grid>

                            {loadingConciliaciones ? (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography>Cargando conciliaciones...</Typography>
                                </Box>
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: 80 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'id'}
                                                        direction={ordenLista.campo === 'id' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('id')}
                                                    >
                                                        ID
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell style={{ width: 120 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'codigo'}
                                                        direction={ordenLista.campo === 'codigo' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('codigo')}
                                                    >
                                                        Código
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell style={{ width: 200 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'banco'}
                                                        direction={ordenLista.campo === 'banco' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('banco')}
                                                    >
                                                        Banco
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell style={{ width: 150 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'cuenta'}
                                                        direction={ordenLista.campo === 'cuenta' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('cuenta')}
                                                    >
                                                        Cuenta Bancaria
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell style={{ width: 250 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'descripcion'}
                                                        direction={ordenLista.campo === 'descripcion' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('descripcion')}
                                                    >
                                                        Descripción
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell style={{ width: 120 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'fechaIngreso'}
                                                        direction={ordenLista.campo === 'fechaIngreso' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('fechaIngreso')}
                                                    >
                                                        Fecha Emisión
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="right" style={{ width: 130 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'saldoContable'}
                                                        direction={ordenLista.campo === 'saldoContable' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('saldoContable')}
                                                    >
                                                        Saldo Contable
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="right" style={{ width: 130 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'saldoBancario'}
                                                        direction={ordenLista.campo === 'saldoBancario' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('saldoBancario')}
                                                    >
                                                        Saldo Bancario
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="right" style={{ width: 130 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'diferencia'}
                                                        direction={ordenLista.campo === 'diferencia' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('diferencia')}
                                                    >
                                                        Diferencia
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="center" style={{ width: 100 }}>
                                                    <TableSortLabel
                                                        active={ordenLista.campo === 'estado'}
                                                        direction={ordenLista.campo === 'estado' ? ordenLista.direccion : 'asc'}
                                                        onClick={() => ordenarLista('estado')}
                                                    >
                                                        Estado
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="center" style={{ width: 280 }}>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {conciliacionesFiltradasOrdenadas.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={11} align="center">
                                                            <Typography variant="body2" color="textSecondary">
                                                                No hay conciliaciones para los filtros actuales
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                conciliacionesFiltradasOrdenadas.map((conc) => (
                                                    <TableRow key={conc.id}>
                                                        <TableCell>{conc.id}</TableCell>
                                                        <TableCell>{conc.codigo || 'N/A'}</TableCell>
                                                        <TableCell>
                                                            {conc.chkBancosNavigation?.nombreBanco || 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                                                                <Typography variant="body2">
                                                                    {obtenerTextoCuentaConciliacion(conc) || 'N/A'}
                                                                </Typography>
                                                                {obtenerDetalleCuentaContableConciliacion(conc) && (
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                        {obtenerDetalleCuentaContableConciliacion(conc)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{conc.descripcion}</TableCell>
                                                        <TableCell>
                                                            {conc.fechaIngreso ? format(new Date(conc.fechaIngreso), 'dd/MM/yyyy') : 'N/A'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <span style={{ whiteSpace: 'nowrap' }}>
                                                                {obtenerSimboloMonedaConciliacion(conc)} {formatearMonto(conc.saldoContable || 0)}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <span style={{ whiteSpace: 'nowrap' }}>
                                                                {obtenerSimboloMonedaConciliacion(conc)} {formatearMonto(conc.saldoBancario || 0)}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <span style={{ 
                                                                color: conc.diferencia === 0 ? 'green' : 'red',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                <span style={{ whiteSpace: 'nowrap' }}>
                                                                    {obtenerSimboloMonedaConciliacion(conc)} {formatearMonto(conc.diferencia || 0)}
                                                                </span>
                                                            </span>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip 
                                                                label={obtenerEstadoConciliacion(conc)}
                                                                size="small"
                                                                color={obtenerEstadoConciliacion(conc) === 'Cuadrada' ? 'success' : 'warning'}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="success"
                                                                    size="small"
                                                                    onClick={() => abrirConciliacionExistente(conc.id)}
                                                                >
                                                                    Conciliar
                                                                </Button>
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    size="small"
                                                                    onClick={() => imprimirConciliacion(conc.id)}
                                                                    disabled={imprimiendoId === conc.id}
                                                                >
                                                                    {imprimiendoId === conc.id ? 'Generando...' : 'Ver Reporte'}
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="error"
                                                                    size="small"
                                                                    onClick={() => eliminarConciliacion(conc.id)}
                                                                >
                                                                    Eliminar
                                                                </Button>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal para visualizar PDF */}
            <Modal open={openModalPdf} onClose={handleCloseModalPdf}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        height: '90vh',
                        bgcolor: 'white',
                        boxShadow: 24,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {/* Header del modal */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            borderBottom: '1px solid #ddd',
                            bgcolor: '#f5f5f5'
                        }}
                    >
                        <Typography variant="h6" component="h2">
                            {pdfNombre}
                        </Typography>
                        <Box>
                            <IconButton
                                onClick={handleDownloadPdf}
                                color="primary"
                                title="Descargar PDF"
                                sx={{ mr: 1 }}
                            >
                                <DownloadIcon />
                            </IconButton>
                            <IconButton
                                onClick={handleCloseModalPdf}
                                color="error"
                                title="Cerrar"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Contenedor del PDF */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'hidden',
                            bgcolor: '#525659'
                        }}
                    >
                        {pdfUrl && (
                            <iframe
                                src={pdfUrl}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none'
                                }}
                                title="Visor de PDF"
                            />
                        )}
                    </Box>

                    {/* Footer del modal */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            p: 2,
                            borderTop: '1px solid #ddd',
                            bgcolor: '#f5f5f5'
                        }}
                    >
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleCloseModalPdf}
                            startIcon={<CloseIcon />}
                        >
                            CERRAR
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
}

export default ConciliacionBancaria;
